import { useEffect, useState } from 'react'
import ReplayCodeEditor from './ReplayCodeEditor'

const WEB_DEV_FILES = [
  { key: 'html', label: 'HTML' },
  { key: 'css', label: 'CSS' },
  { key: 'js', label: 'JavaScript' },
]

// Renders replayed/revisited code read-only. GUI sessions log `code` as a
// JSON-stringified {html,css,js} blob (see CodeEditor.jsx's isWebDev branch), not a plain
// source string, so rendering it in a single pane with Python highlighting - what
// ReplayWorkspace.jsx did originally - shows garbled JSON text instead of the student's
// actual HTML/CSS/JS.
//
// The real editor (CodeEditor.jsx) never shows all three files at once either - it's a
// single pane switched by HTML/CSS/JS tabs, one active at a time. An earlier version of
// this fix rendered all three stacked vertically, which doesn't match what the student's
// screen actually looked like and buries whichever file (usually JS) sorts last. This
// mirrors the real tab bar instead, defaulting to whichever file was actually active in
// the telemetry at the current point in the replay (activeFile, from the CODE_TYPE
// event's active_file field) - snapping back to it whenever it changes, the same way the
// student's own screen would have - while still letting the viewer click another tab to
// peek at its current state without that being mistaken for what was on screen.
//
// `stacked` is the one deliberate exception to all of the above: InitialFinalJS's live
// editor (CodeEditor.jsx's `stacked` prop) genuinely does show all three files at once, so
// for that type ONLY, matching the tabbed view here would be the thing that no longer
// matches the student's real screen. Defaults false for every other GUI type, which keeps
// this exact behavior unchanged for them.
export default function ReplayCodeView({ code = '', isGuiSession = false, activeFile, enabledFiles = null, paneTitle = null, stacked = false, changedLines = null }) {
  const [manualTab, setManualTab] = useState(null)

  useEffect(() => {
    setManualTab(null)
  }, [activeFile])

  if (isGuiSession) {
    let webDevFiles = null
    try {
      webDevFiles = JSON.parse(code)
    } catch (e) {
      webDevFiles = null
    }
    if (webDevFiles && typeof webDevFiles === 'object') {
      // enabledFiles mirrors the live CodeEditor's hideDisabledTabs - InitialFinal/
      // InitialFinalCSS sessions only ever showed the student one file (HTML or CSS), even
      // though the underlying code blob still carries all three (the others are the
      // scaffold, not something the student edited) - without this the replay showed tabs
      // the student never had.
      const availableFiles = WEB_DEV_FILES.filter((f) =>
        webDevFiles[f.key] !== undefined && (!enabledFiles || enabledFiles.includes(f.key))
      )
      if (stacked) {
        return (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {paneTitle && (
              <div style={{ padding: '8px 12px 0 12px', fontSize: '0.78rem', fontWeight: 600, color: '#737373', background: '#fafafa' }}>
                {paneTitle}
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {availableFiles.map((f, i) => (
                <div key={f.key} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderTop: i > 0 ? '1px solid #e5e5e5' : 'none' }}>
                  <div style={{
                    padding: '6px 12px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em',
                    textTransform: 'uppercase', color: '#171717', background: '#fafafa', borderBottom: '1px solid #e5e5e5',
                  }}>
                    {f.label}
                  </div>
                  {/* overflow: hidden is load-bearing here, not decorative - ReplayCodeEditor's
                      root (.ws-editor-container) sets flex:1 expecting a FLEX parent, but this
                      wrapper is display:block (unset), so that flex property has no effect and
                      CodeMirror instead sizes to its natural content height. With three panes
                      stacked, that meant each one grew past its allotted third and overlapped
                      the next - clipping here (CodeMirror's own .cm-scroller is already
                      overflow:auto internally) turns that into a normal per-file scrollbar
                      instead. */}
                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <ReplayCodeEditor code={webDevFiles[f.key] || ''} language={f.key === 'js' ? 'javascript' : f.key} scrollable />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      }
      const activeTab = manualTab || activeFile || availableFiles[0]?.key || 'html'
      return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {paneTitle && (
            <div style={{
              padding: '8px 12px 0 12px',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: '#737373',
              background: '#fafafa',
            }}>
              {paneTitle}
            </div>
          )}
          <div style={{
            display: 'flex',
            background: '#fafafa',
            borderBottom: '1px solid #e5e5e5',
            padding: '6px 8px 0 8px',
            gap: '4px',
          }}>
            {availableFiles.map((f) => (
              <button
                key={f.key}
                onClick={() => setManualTab(f.key)}
                style={{
                  padding: '6px 14px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  border: '1px solid #e5e5e5',
                  borderBottom: f.key === activeTab ? '1px solid #ffffff' : 'none',
                  borderRadius: '6px 6px 0 0',
                  backgroundColor: f.key === activeTab ? '#ffffff' : '#fafafa',
                  color: f.key === activeTab ? '#171717' : '#737373',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  zIndex: f.key === activeTab ? 2 : 1,
                  textTransform: 'uppercase',
                  outline: 'none',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ReplayCodeEditor
              scrollable
              code={webDevFiles[activeTab] || ''}
              language={activeTab === 'js' ? 'javascript' : activeTab}
            />
          </div>
        </div>
      )
    }
    // No CODE_* event logged yet, or code isn't valid JSON - fall back to a single
    // HTML-highlighted pane rather than defaulting to Python.
    return <ReplayCodeEditor code={code} language="html" scrollable />
  }
  return <ReplayCodeEditor code={code} language="python" changedLines={changedLines} scrollable />
}
